import React, { useEffect, useState } from 'react';
import FormShell from './FormShell';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { loadSavedForm, saveForm } from './formUtils';

const DEFAULTS = {
  proposedMark1: '',
  proposedMark2: '',
  proposedMark3: '',
  classNumber: '',
  goodsDescription: '',
  applicantType: 'individual',
  applicantName: '',
  applicantId: '',
  contactEmail: '',
  contactPhone: '',
  hasUsedMark: 'no',
  firstUseDate: '',
};

const steps = ['Mark Details', 'Applicant & Class', 'Review'];

const TrademarkForm = ({ serviceId, onComplete }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => loadSavedForm(serviceId) || DEFAULTS);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    saveForm(serviceId, form);
    setSaved(true);
    const t = setTimeout(() => setSaved(false), 1500);
    return () => clearTimeout(t);
  }, [serviceId, form]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (step === 1 && !form.proposedMark1) {
      toast({
        title: 'Required Field Missing',
        description: 'Provide at least one proposed word mark.',
        variant: 'destructive',
      });
      return false;
    }
    if (step === 2) {
      if (!form.classNumber || !form.goodsDescription) {
        toast({
          title: 'Required Fields Missing',
          description: 'Class number and description of goods/services are required.',
          variant: 'destructive',
        });
        return false;
      }
      if (!form.applicantName || !form.contactEmail) {
        toast({
          title: 'Required Fields Missing',
          description: 'Applicant name and contact email are required.',
          variant: 'destructive',
        });
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    setStep((s) => Math.min(s + 1, steps.length));
    window.scrollTo(0, 0);
  };
  const prev = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };
  const submit = () => {
    if (!validate()) return;
    onComplete({ ...form, option: 'A — Word mark only' });
  };

  return (
    <FormShell
      title="Trademark Registration (Option A — Word Mark)"
      stepLabels={steps}
      currentStep={step}
      onNext={next}
      onPrevious={prev}
      onSubmit={submit}
      autoSaved={saved}
    >
      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Proposed Word Mark</h3>
          <p className="text-sm text-gray-600">
            Provide up to 3 alternatives. We&apos;ll search availability in order before lodging
            the application.
          </p>
          <div>
            <Label>1st Choice Word Mark *</Label>
            <Input value={form.proposedMark1} onChange={(e) => set('proposedMark1', e.target.value)} />
          </div>
          <div>
            <Label>2nd Choice Word Mark</Label>
            <Input value={form.proposedMark2} onChange={(e) => set('proposedMark2', e.target.value)} />
          </div>
          <div>
            <Label>3rd Choice Word Mark</Label>
            <Input value={form.proposedMark3} onChange={(e) => set('proposedMark3', e.target.value)} />
          </div>
          <div>
            <Label>Have you already used the mark in trade?</Label>
            <select
              value={form.hasUsedMark}
              onChange={(e) => set('hasUsedMark', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="no">No (intent to use)</option>
              <option value="yes">Yes</option>
            </select>
          </div>
          {form.hasUsedMark === 'yes' && (
            <div>
              <Label>Date of First Use</Label>
              <Input
                type="date"
                value={form.firstUseDate}
                onChange={(e) => set('firstUseDate', e.target.value)}
              />
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Class &amp; Applicant</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Nice Class Number *</Label>
              <Input
                placeholder="e.g. 35 (advertising / business)"
                value={form.classNumber}
                onChange={(e) => set('classNumber', e.target.value)}
              />
            </div>
            <div>
              <Label>Applicant Type</Label>
              <select
                value={form.applicantType}
                onChange={(e) => set('applicantType', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="individual">Individual</option>
                <option value="company">Company / Pty Ltd</option>
                <option value="cc">Close Corporation</option>
                <option value="trust">Trust</option>
              </select>
            </div>
          </div>
          <div>
            <Label>Description of Goods / Services *</Label>
            <textarea
              className="mt-1 w-full p-2 border border-gray-300 rounded-md min-h-[100px]"
              value={form.goodsDescription}
              onChange={(e) => set('goodsDescription', e.target.value)}
            />
          </div>
          <div>
            <Label>Applicant Name *</Label>
            <Input
              value={form.applicantName}
              onChange={(e) => set('applicantName', e.target.value)}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>ID / Registration Number</Label>
              <Input value={form.applicantId} onChange={(e) => set('applicantId', e.target.value)} />
            </div>
            <div>
              <Label>Contact Email *</Label>
              <Input
                type="email"
                value={form.contactEmail}
                onChange={(e) => set('contactEmail', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Contact Phone</Label>
            <Input value={form.contactPhone} onChange={(e) => set('contactPhone', e.target.value)} />
          </div>
        </div>
      )}

      {step === 3 && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-emerald-800">Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><strong>Option:</strong> A — Word mark only</p>
            <p><strong>1st Choice:</strong> {form.proposedMark1}</p>
            {form.proposedMark2 && <p><strong>2nd:</strong> {form.proposedMark2}</p>}
            {form.proposedMark3 && <p><strong>3rd:</strong> {form.proposedMark3}</p>}
            <p><strong>Class:</strong> {form.classNumber}</p>
            <p><strong>Applicant:</strong> {form.applicantName} ({form.applicantType})</p>
            <p><strong>Contact:</strong> {form.contactEmail}</p>
          </CardContent>
        </Card>
      )}
    </FormShell>
  );
};

export default TrademarkForm;
